import { DATA_SOURCES } from '@/config/constants';
import { useEntity } from 'utils/hooks/api';
import useEntityBase, { isEmptyRow } from './useEntityBase';
import { useGoogleData } from 'utils/hooks';

const NO_WEEK = {
    week: undefined,
    year: undefined,
    query: { isLoading: false },
};

/**
 * One Google entity - a brand or a category - with its row.
 *
 * `country` is the caller's: most pages read `useCurrentCountry`, and the two
 * brand pages still read the geo reducer, which lags it by a render.
 */
const useGoogleEntity = ({
    entityType,
    entityId,
    country,
    controller,
    minimal,
    coverage,
}) => {
    const entityQuery = useEntity({
        id: entityId,
        entityType,
        dataSource: DATA_SOURCES.GOOGLE,
        minimal,
    });
    const entity = entityQuery.data;

    const rowQuery = useGoogleData({
        enabled: !!(entityId && country),
        data: {
            [entityType]: entityId ? [entityId] : null,
            countries: country ? [country.id] : null,
        },
        paginateBy: entityType,
        /* A row for an entity with no data would make every page's emptiness
           rule a question about the row's contents rather than its presence. */
        includeNoDataItems: false,
        controller,
    });
    const row = rowQuery.data?.[0];
    const details = entity || row || null;
    const rowsFetching = rowQuery.data?.some(
        (d) => d.fetching || d.fetchingData,
    );

    return useEntityBase({
        dataSource: DATA_SOURCES.GOOGLE,
        slug: 'google',
        entityType,
        entityId,
        controller,
        entity,
        entityQuery,
        details,
        country,
        week: NO_WEEK.week,
        year: NO_WEEK.year,
        weeksQuery: NO_WEEK.query,
        row,
        rowQuery,
        series: row?.time_series,
        name: details?.name,
        isLoading:
            rowQuery.isLoading ||
            entityQuery.isLoading ||
            !country ||
            rowsFetching,
        isEmpty: isEmptyRow({ rowQuery, row, volumeKey: 'volume' }),
        coverage,
    });
};

/** The siblings the category score card compares this entity against. */
export const useGoogleSiblings = ({ parentCategory, country, controller }) => {
    const query = useGoogleData({
        enabled: !!(parentCategory && country),
        data: {
            categories: parentCategory ? parentCategory.children : null,
            countries: country ? [country.id] : null,
        },
        paginateBy: 'categories',
        controller,
    });
    const siblings = query.data;
    return {
        siblings,
        ready: !!(
            siblings &&
            !siblings.some(
                ({ fetching, fetchingData }) => fetching || fetchingData,
            )
        ),
    };
};

export default useGoogleEntity;
